export interface KostenGuide {
  slug: string;
  title: string;
  description: string;
  socialTitle: string;
  socialDescription: string;
  shortAnswer: string;
  priceLabel: string;
  price: string;
  priceNoot: string;
  factoren: string[];
  faq: { vraag: string; antwoord: string }[];
  tips?: string[];
  /** Prijstabel met concrete varianten en richtprijzen. */
  tabelTitel?: string;
  tabel?: { label: string; prijs: string; noot: string }[];
  /** Lokale prijsindicatie per plaats in de Drechtsteden. */
  lokaalTitel?: string;
  lokaal?: { plaats: string; prijs: string; noot: string }[];
}

export const kostenGuides: Record<string, KostenGuide> = {
  dakkapel: {
    tabelTitel: "Dakkapel kosten per breedte",
    tabel: [
      { label: "Dakkapel 1,5 meter", prijs: "€ 6.000 – € 8.500", noot: "Eén raam, geschikt voor een slaap- of werkkamer." },
      { label: "Dakkapel 2,5 meter", prijs: "€ 8.000 – € 11.000", noot: "Meest gekozen maat, twee ramen." },
      { label: "Dakkapel 3 meter", prijs: "€ 9.000 – € 13.000", noot: "Ruime kamer met stahoogte over de volle breedte." },
      { label: "Dakkapel 4 meter", prijs: "€ 11.500 – € 15.000", noot: "Vaak over de volle breedte van een rijwoning." },
      { label: "Dakkapel 5 meter of meer", prijs: "vanaf € 15.000", noot: "Zwaardere constructie en extra hijswerk." },
    ],
    lokaalTitel: "Dakkapel prijsindicatie in de Drechtsteden",
    lokaal: [
      { plaats: "Dordrecht", prijs: "€ 7.500 – € 15.000", noot: "Geen voorrijkosten; bij monumenten en beschermd stadsgezicht is een vergunning nodig." },
      { plaats: "Zwijndrecht", prijs: "€ 7.500 – € 15.000", noot: "Veel naoorlogse rijwoningen waar een dakkapel over de volle breedte past." },
      { plaats: "Papendrecht", prijs: "€ 7.500 – € 15.000", noot: "Achterzijde meestal vergunningvrij binnen het omgevingsplan." },
      { plaats: "Sliedrecht", prijs: "€ 7.750 – € 15.500", noot: "Iets hogere prijs door reistijd en aanvoer van materiaal." },
    ],
    slug: "dakkapel",
    title: "Dakkapel kosten 2026: wat kost een dakkapel?",
    description:
      "Wat kost een dakkapel in 2026? Richtprijzen voor hout, kunststof en aluminium, inclusief plaatsing en afwerking door een timmerman uit Dordrecht.",
    socialTitle: "Dakkapel kosten 2026: wat kost een dakkapel?",
    socialDescription:
      "Richtprijzen voor hout, kunststof en aluminium, inclusief plaatsing en afwerking.",
    shortAnswer:
      "Een dakkapel kost in 2026 gemiddeld € 7.500 tot € 15.000, inclusief materiaal, plaatsing en afwerking. De exacte prijs hangt af van de breedte, het materiaal, de afwerking en de bereikbaarheid van het dak.",
    priceLabel: "Richtprijs dakkapel",
    price: "€ 7.500 – € 15.000",
    priceNoot: "Voor een dakkapel van circa 2 tot 4 meter breed, inclusief plaatsing en afwerking.",
    factoren: [
      "Breedte van de dakkapel en aantal ramen",
      "Materiaal en afwerking (hout, kunststof, aluminium)",
      "Isolatiewaarde en type glas",
      "Bereikbaarheid van het dak (steiger of hoogwerker)",
      "Dakbedekking en waterafvoer rondom de dakkapel",
    ],
    faq: [
      {
        vraag: "Wat kost een dakkapel van 3 meter breed?",
        antwoord:
          "Voor een dakkapel van 3 meter breed reken je gemiddeld op € 9.000 tot € 13.000, afhankelijk van materiaal en afwerking. Hout is doorgaans voordeliger dan kunststof of aluminium.",
      },
      {
        vraag: "Hoe lang duurt het plaatsen van een dakkapel?",
        antwoord:
          "Een dakkapel op maat wordt vaak binnen 3 tot 5 dagen geplaatst. Dit is inclusief het maken van de kap, plaatsen van het kozijn en het afwerken van de binnenkant.",
      },
      {
        vraag: "Is een dakkapel vergunningvrij?",
        antwoord:
          "In veel gevallen is een dakkapel aan de achterzijde vergunningvrij, mits deze binnen de regels van het bestemmingsplan valt. We controleren dit samen vooraf.",
      },
      {
        vraag: "Kan ik subsidie krijgen op een dakkapel?",
        antwoord:
          "Niet direct op de dakkapel zelf, maar als je de dakkapel combineert met isolatie van het dak of nieuwe kozijnen met HR++ glas, kan dat wel meetellen voor verduurzamingssubsidie.",
      },
    ],
    tips: [
      "Kies hout voor een klassieke uitstraling en een lager starttarief; kunststof en aluminium vragen minder onderhoud.",
      "Combineer de dakkapel met dakisolatie; dat verlaagt je energierekening en kan meetellen voor subsidie.",
      "Laat direct een goede waterafvoer en dakbedekking rondom de dakkapel aanleggen; dit voorkomt lekkages later.",
    ],
  },
  kozijnen: {
    tabelTitel: "Kozijn kosten per type",
    tabel: [
      { label: "Houten kozijn met HR++ glas", prijs: "€ 900 – € 1.500", noot: "Op maat gemaakt profiel, ook voor oudere woningen." },
      { label: "Kunststof kozijn met HR++ glas", prijs: "€ 950 – € 1.600", noot: "Onderhoudsarm, standaard in naoorlogse wijken." },
      { label: "Aluminium kozijn", prijs: "€ 1.300 – € 2.200", noot: "Slanke profielen, veel glasoppervlak." },
      { label: "Schuifpui 3 meter", prijs: "€ 4.500 – € 8.000", noot: "Inclusief plaatsing, afwerking en HR++ glas." },
      { label: "Complete woning (8 kozijnen)", prijs: "€ 7.500 – € 14.500", noot: "Per verdieping ingepland, inclusief afvoeren oud materiaal." },
    ],
    lokaalTitel: "Kozijnen prijsindicatie in de Drechtsteden",
    lokaal: [
      { plaats: "Dordrecht", prijs: "€ 900 – € 1.800 per kozijn", noot: "In het beschermd stadsgezicht maken we het profiel exact na." },
      { plaats: "Zwijndrecht", prijs: "€ 900 – € 1.800 per kozijn", noot: "Vervanging in dezelfde maat is er meestal vergunningvrij." },
      { plaats: "Papendrecht", prijs: "€ 900 – € 1.800 per kozijn", noot: "Veel woningen met verouderd dubbelglas uit de jaren '70." },
      { plaats: "Sliedrecht", prijs: "€ 950 – € 1.850 per kozijn", noot: "Iets hogere prijs door reistijd." },
    ],
    slug: "kozijnen",
    title: "Kozijnen kosten 2026: wat kost een kozijn?",
    description:
      "Wat kosten nieuwe kozijnen in 2026? Richtprijzen per kozijn voor hout, kunststof en aluminium, inclusief HR++ glas en plaatsing in Dordrecht.",
    socialTitle: "Kozijnen kosten 2026: wat kost een kozijn?",
    socialDescription:
      "Richtprijzen per kozijn voor hout, kunststof en aluminium, inclusief HR++ glas en plaatsing.",
    shortAnswer:
      "Nieuwe kozijnen kosten in 2026 gemiddeld € 900 tot € 1.800 per kozijn, inclusief HR++ glas en plaatsing. Hout ligt vaak aan de onderkant, aluminium aan de bovenkant.",
    priceLabel: "Richtprijs per kozijn",
    price: "€ 900 – € 1.800",
    priceNoot:
      "Inclusief HR++ glas en plaatsing; grote of samengestelde kozijnen liggen hoger.",
    factoren: [
      "Aantal kozijnen en maatvoering",
      "Materiaal: hout, kunststof of aluminium",
      "Glaskeuze: HR++ of triple",
      "Draai-kiepdelen, roosters en horren",
      "Bereikbaarheid en hoeveelheid sloopwerk",
    ],
    faq: [
      {
        vraag: "Wat is goedkoper: houten of kunststof kozijnen?",
        antwoord:
          "Houten kozijnen zijn vaak goedkoper in aanschaf, maar vragen meer onderhoud. Kunststof kozijnen kosten iets meer, maar zijn onderhoudsarm en gaan lang mee.",
      },
      {
        vraag: "Hoeveel kozijnen vervangen in een gemiddelde woning?",
        antwoord:
          "Een tussenwoning heeft vaak 6 tot 10 kozijnen. Bij een prijs van € 900 tot € 1.800 per kozijn komt het totaal uit op € 5.400 tot € 18.000.",
      },
      {
        vraag: "Verhoogt HR++ glas de kosten veel?",
        antwoord:
          "HR++ glas is inbegrepen in de richtprijs. Triple glas is isolerender maar kost € 100 tot € 300 meer per kozijn, afhankelijk van het formaat.",
      },
      {
        vraag: "Kom ik in aanmerking voor subsidie op kozijnen?",
        antwoord:
          "Ja, nieuwe kozijnen met isolerend glas vallen onder verduurzaming. Onze offertes en facturen zijn geschikt voor je subsidieaanvraag.",
      },
    ],
    tips: [
      "Vervang kozijnen en glas tegelijk; oude beglazing in een nieuw kozijn is zonde van het werk.",
      "Kies voor een aantal draai-kiepramen; die geven extra ventilatiemogelijkheden zonder extra kosten.",
      "Vraag een offerte aan voor de hele woning; vaak krijg je dan een scherpere prijs per kozijn.",
    ],
  },
  renovatie: {
    tabelTitel: "Renovatie kosten per onderdeel",
    tabel: [
      { label: "Badkamer renoveren", prijs: "€ 8.000 – € 18.000", noot: "Inclusief sloopwerk, tegelwerk en installaties." },
      { label: "Keuken plaatsen (montage)", prijs: "€ 1.500 – € 3.500", noot: "Exclusief keuken en apparatuur." },
      { label: "Vloer- en dakisolatie", prijs: "€ 45 – € 90 per m²", noot: "Vaak subsidiabel bij twee maatregelen." },
      { label: "Binnenwand plaatsen of verwijderen", prijs: "€ 900 – € 2.500", noot: "Bij dragende wanden komt een stalen balk erbij." },
      { label: "Complete verbouwing tussenwoning", prijs: "€ 45.000 – € 120.000", noot: "Afhankelijk van omvang, installaties en afwerkniveau." },
    ],
    lokaalTitel: "Renovatie prijsindicatie in de Drechtsteden",
    lokaal: [
      { plaats: "Dordrecht", prijs: "€ 45.000 – € 120.000", noot: "Bij vooroorlogse woningen extra aandacht voor houtrot en vloeren." },
      { plaats: "Zwijndrecht", prijs: "€ 45.000 – € 115.000", noot: "Naoorlogse woningen: vaak isolatie plus kozijnen combineren." },
      { plaats: "Papendrecht", prijs: "€ 45.000 – € 115.000", noot: "Veel woningen uit de jaren '70 met verouderde installaties." },
      { plaats: "Sliedrecht", prijs: "€ 46.000 – € 120.000", noot: "Iets hogere prijs door reistijd en materiaalaanvoer." },
    ],
    slug: "renovatie",
    title: "Renovatie kosten 2026: wat kost een verbouwing?",
    description:
      "Wat kost een renovatie in 2026? Richtprijzen per m² voor het renoveren van je woning in Dordrecht, van één ruimte tot complete verbouwing.",
    socialTitle: "Renovatie kosten 2026: wat kost een verbouwing?",
    socialDescription:
      "Richtprijzen per m² voor het renoveren van je woning, van één ruimte tot complete verbouwing.",
    shortAnswer:
      "Een renovatie kost in 2026 gemiddeld € 400 tot € 900 per m². Voor een complete verbouwing van een tussenwoning komt dat neer op € 40.000 tot € 90.000, afhankelijk van het afwerkniveau.",
    priceLabel: "Richtprijs renovatie",
    price: "€ 400 – € 900 per m²",
    priceNoot: "Van één ruimte opknappen tot een volledige verbouwing met nieuwe indeling.",
    factoren: [
      "Omvang: één ruimte of de hele woning",
      "Sloopwerk en afvoer",
      "Nieuwe indeling en constructieve ingrepen",
      "Verduurzaming — mogelijk met subsidie",
      "Afwerkingsniveau en keuze van materialen",
    ],
    faq: [
      {
        vraag: "Wat kost een complete renovatie van een tussenwoning?",
        antwoord:
          "Voor een complete renovatie van een tussenwoning reken je op € 40.000 tot € 90.000. Dit is inclusief sloopwerk, nieuwe indeling, installaties en afwerking.",
      },
      {
        vraag: "Kan ik subsidie krijgen voor een renovatie?",
        antwoord:
          "Ja, als de renovatie de woning verduurzaamt — bijvoorbeeld door isolatie, nieuwe kozijnen of warmtepompvoorbereiding — kom je vaak in aanmerking voor subsidie.",
      },
      {
        vraag: "Hoe lang duurt een complete renovatie?",
        antwoord:
          "Een complete renovatie duurt gemiddeld 8 tot 16 weken, afhankelijk van de omvang en of er vergunningen nodig zijn.",
      },
      {
        vraag: "Wat is het verschil tussen renovatie en verbouw?",
        antwoord:
          "Renovatie richt zich op het vernieuwen en verbeteren van bestaande ruimtes. Verbouw gaat verder en verandert vaak de indeling of voegt ruimte toe, zoals bij een aanbouw of dakkapel.",
      },
    ],
    tips: [
      "Plan verduurzaming direct mee; subsidie en lagere energiekosten maken dit vaak rendabel.",
      "Laat een constructieberekening maken bij het doorbreken van draagmuren.",
      "Werk in fasen als het budget dat vraagt; zo kun je later verder zonder werk te moeten herdoen.",
    ],
  },
};
